import { useState } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuditLog } from '@/lib/queries';

const ACTION_STYLES: Record<string, string> = {
  CREATE: 'bg-accent-light text-accent',
  UPDATE: 'bg-blue-50 text-info',
  DELETE: 'bg-red-50 text-error',
  LOGIN: 'bg-primary-subtle text-primary',
  LOGOUT: 'bg-gray-100 text-gray-600',
};

function exportCsv(items: any[]) {
  const header = ['Fecha', 'Usuario', 'Accion', 'Modulo', 'Entidad', 'IP'];
  const rows = items.map((a) => [
    new Date(a.createdAt).toLocaleString('es'),
    a.user?.email ?? '',
    a.action,
    a.module,
    a.metadata?.id ?? a.metadata?.event ?? '',
    a.metadata?.ip ?? '',
  ]);
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? '');
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Creó',
  UPDATE: 'Editó',
  DELETE: 'Eliminó',
  LOGIN: 'Ingresó',
  LOGOUT: 'Salió',
};

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [module, setModule] = useState('');
  const pageSize = 10;
  const { data, isLoading } = useAuditLog({
    module: module || undefined,
    page,
    pageSize,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Registro de Auditoría</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Historial de acciones realizadas en el sistema
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCsv(data?.items ?? [])}
            className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
          >
            <Download size={14} /> Exportar CSV
          </button>
          <button
            onClick={() => window.print()}
            className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
          >
            <Download size={14} /> Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <select className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary">
            <option>Todos los usuarios</option>
          </select>
          <select
            value={module}
            onChange={(e) => {
              setModule(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            <option value="">Todos los módulos</option>
            <option value="auth">Autenticación</option>
            <option value="employees">Colaboradores</option>
            <option value="leave_requests">Permisos</option>
            <option value="users">Usuarios</option>
            <option value="catalogs.countries">Países</option>
            <option value="catalogs.departments">Departamentos</option>
            <option value="catalogs.positions">Puestos</option>
            <option value="catalogs.holidays">Feriados</option>
          </select>
          <input
            type="date"
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="px-6 py-3">Fecha/Hora</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Acción</th>
                <th className="px-4 py-3">Módulo</th>
                <th className="px-4 py-3">Entidad</th>
                <th className="px-4 py-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Cargando...
                  </td>
                </tr>
              )}
              {data?.items?.map((a: any) => (
                <tr key={a.id} className="border-b border-border-light">
                  <td className="px-6 py-4 font-mono text-xs text-font-secondary">
                    {new Date(a.createdAt).toLocaleString('es')}
                  </td>
                  <td className="px-4 py-4 text-font-primary">
                    {a.user?.email ?? '–'}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        ACTION_STYLES[a.action] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {ACTION_LABELS[a.action] ?? a.action}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-font-secondary">{a.module}</td>
                  <td className="px-4 py-4 text-font-secondary">
                    {a.metadata?.id ?? a.metadata?.event ?? '–'}
                  </td>
                  <td className="px-4 py-4 font-mono text-xs text-font-tertiary">
                    {a.metadata?.ip ?? '–'}
                  </td>
                </tr>
              ))}
              {!isLoading && data?.items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Sin registros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <p className="text-xs text-font-secondary">
            Mostrando {data?.items?.length ?? 0} de {total} registros
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(
              (p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold ${
                    p === page
                      ? 'bg-primary text-white'
                      : 'border border-border text-font-secondary hover:bg-bg'
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
